import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import { getCandidateDisplayName, type Candidate } from "./candidate-views";

export type CandidateTarget = { userId: string; name: string };

export type PoolRemovalTarget = {
  providerPoolId: string;
  poolName: string;
  userId: string;
  candidateName: string;
};

export type ChatTarget = {
  id: string;
  firstName: string | null;
  lastName: string | null;
};

const FALLBACK_JOB_TITLE = "this position";
const FALLBACK_FIRST_NAME = "there";

function buildRejectionMessage({
  candidate,
  jobTitle,
  jobId,
}: {
  candidate: Candidate;
  jobTitle: string;
  jobId: string;
}): string {
  const jobUrl = `${window.location.origin}/provider/jobs/${jobId}`;
  return `Hi ${candidate.firstName ?? FALLBACK_FIRST_NAME},\n\nThank you for your interest in the ${jobTitle || FALLBACK_JOB_TITLE} role (${jobUrl}). After careful consideration, we've decided to move forward with other candidates at this time.\n\nWe appreciate you taking the time to apply and wish you the best in your career.\n\nBest regards`;
}

function toTarget(candidate: Candidate): CandidateTarget {
  return {
    userId: candidate.userId,
    name: getCandidateDisplayName(candidate),
  };
}

// Owns every dialog's open state and every mutation on the job dashboard so
// the page component only has to wire queries to views.
export function useJobDashboardActions({
  jobId,
  jobTitle,
}: {
  jobId: string;
  jobTitle: string | null;
}) {
  const trpc = useTRPC();
  const router = useRouter();
  const orgPath = useOrgPath();
  const { selectedOrgBusinessUnit } = useOrganization();
  const organizationBusinessUnitId = selectedOrgBusinessUnit.id;

  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showProfileUserId, setShowProfileUserId] = useState<string | null>(
    null,
  );
  const [notesTarget, setNotesTarget] = useState<CandidateTarget | null>(null);
  const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
  const [addToPoolUserId, setAddToPoolUserId] = useState<string | null>(null);
  const [removeFromPoolTarget, setRemoveFromPoolTarget] =
    useState<PoolRemovalTarget | null>(null);
  const [hireTarget, setHireTarget] = useState<CandidateTarget | null>(null);
  const [hireStartDate, setHireStartDate] = useState("");
  const [markNotHiredTarget, setMarkNotHiredTarget] =
    useState<CandidateTarget | null>(null);
  const [rejectTarget, setRejectTarget] = useState<CandidateTarget | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");
  const [sendRejectionMessage, setSendRejectionMessage] = useState(true);
  const [rejectionMessage, setRejectionMessage] = useState("");

  const invalidateCandidates = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.jobPost.getInterestedCandidates.queryKey(),
    });
  const invalidateAffiliations = () =>
    queryClient.invalidateQueries({
      queryKey:
        trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryKey({
          organizationBusinessUnitId,
        }),
    });

  const resetHireDialog = () => {
    setHireTarget(null);
    setHireStartDate("");
  };

  const resetRejectDialog = () => {
    setRejectTarget(null);
    setRejectReason("");
    setSendRejectionMessage(true);
    setRejectionMessage("");
  };

  const hireMutation = useMutation(
    trpc.organizationBusinessUnitAffiliation.hire.mutationOptions({
      onSuccess: async () => {
        toast.success("Candidate hired successfully.");
        resetHireDialog();
        await Promise.all([
          invalidateCandidates(),
          invalidateAffiliations(),
          queryClient.invalidateQueries({ queryKey: ["profileListInfinite"] }),
        ]);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to hire candidate.");
      },
    }),
  );

  const markNotHiredMutation = useMutation(
    trpc.organizationBusinessUnitAffiliation.markNotHired.mutationOptions({
      onSuccess: async () => {
        toast.success("Candidate is no longer marked as hired.");
        setMarkNotHiredTarget(null);
        await Promise.all([invalidateCandidates(), invalidateAffiliations()]);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to mark candidate not hired.");
      },
    }),
  );

  const createChatThreadMutation = useMutation(
    trpc.chat.orgCreateChatThread.mutationOptions({}),
  );
  const sendChatMessageMutation = useMutation(
    trpc.chat.sendMessage.mutationOptions({}),
  );

  const setCandidateRejectionMutation = useMutation(
    trpc.jobPost.setCandidateRejection.mutationOptions({
      onSuccess: async () => {
        await invalidateCandidates();
      },
      onError: () => {
        toast.error("Failed to update candidate status.");
      },
    }),
  );

  const rejectCandidateMutation = useMutation({
    mutationFn: async (input: {
      userId: string;
      reason?: string;
      message?: string;
    }) => {
      await setCandidateRejectionMutation.mutateAsync({
        organizationBusinessUnitId,
        jobPostId: jobId,
        userId: input.userId,
        rejected: true,
        reason: input.reason,
      });

      if (input.message) {
        const thread = await createChatThreadMutation.mutateAsync({
          userId: input.userId,
          organizationBusinessUnitId,
        });
        await sendChatMessageMutation.mutateAsync({
          threadId: thread.id,
          orgBusinessUnitId: organizationBusinessUnitId,
          message: input.message,
        });
      }
    },
    onSuccess: () => {
      resetRejectDialog();
    },
    onError: (_error, variables) => {
      toast.error(
        variables.message
          ? "Candidate was rejected but the chat message failed to send."
          : "Failed to reject candidate.",
      );
    },
  });

  const setOpenJobMutation = useMutation(
    trpc.jobPost.setOpen.mutationOptions({
      onSuccess: async (_data, variables) => {
        setConfirmClose(false);
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: trpc.jobPost.getMany.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.jobPost.orgGet.queryKey({
              id: jobId,
              organizationBusinessUnitId,
            }),
          }),
        ]);
        if (variables.open) {
          toast.success("Job re-opened.");
        }
      },
      onError: () => {
        toast.error("Failed to update job status.");
      },
    }),
  );

  const deleteJobMutation = useMutation(
    trpc.jobPost.delete.mutationOptions({
      onSuccess: async () => {
        setConfirmDelete(false);
        await queryClient.invalidateQueries({
          queryKey: trpc.jobPost.getMany.queryKey(),
        });
        await router.navigate({ to: orgPath("/jobs/post") });
      },
    }),
  );

  const removeFromPoolMutation = useMutation(
    trpc.providerPool.removeMember.mutationOptions({
      onSuccess: async () => {
        setRemoveFromPoolTarget(null);
        await invalidateCandidates();
        toast.success("Removed from pool.");
      },
      onError: () => {
        toast.error("Failed to remove from pool.");
      },
    }),
  );

  // The card's stage menu and the sheet's checklist both move candidates
  // between stages, so both the checklist data and the candidate rows (which
  // drive card stage labels and sidebar counts) must refresh together.
  const transitionStageMutation = useMutation(
    trpc.hiringProcess.transitionStage.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          invalidateCandidates(),
          queryClient.invalidateQueries(
            trpc.hiringProcess.listCandidates.queryOptions({
              organizationBusinessUnitId,
              jobPostId: jobId,
            }),
          ),
        ]);
        toast.success("Stage updated");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to transition stage");
      },
    }),
  );

  return {
    // Dialog state
    confirmClose,
    setConfirmClose,
    confirmDelete,
    setConfirmDelete,
    showProfileUserId,
    notesTarget,
    chatTarget,
    addToPoolUserId,
    removeFromPoolTarget,
    hireTarget,
    hireStartDate,
    setHireStartDate,
    markNotHiredTarget,
    rejectTarget,
    rejectReason,
    setRejectReason,
    sendRejectionMessage,
    setSendRejectionMessage,
    rejectionMessage,
    setRejectionMessage,
    // Dialog openers and closers
    openProfile: (candidate: Candidate) =>
      setShowProfileUserId(candidate.userId),
    closeProfile: () => setShowProfileUserId(null),
    openNotes: (candidate: Candidate) => setNotesTarget(toTarget(candidate)),
    closeNotes: () => setNotesTarget(null),
    openChat: (candidate: Candidate) =>
      setChatTarget({
        id: candidate.userId,
        firstName: candidate.firstName ?? "",
        lastName: candidate.lastName ?? "",
      }),
    // Messages sent in the dialog change the card's last-message label and the
    // messaged filter, so refresh candidates when it closes.
    closeChat: () => {
      setChatTarget(null);
      void invalidateCandidates();
    },
    openAddToPool: (candidate: Candidate) =>
      setAddToPoolUserId(candidate.userId),
    closeAddToPool: () => setAddToPoolUserId(null),
    openRemoveFromPool: (target: PoolRemovalTarget) =>
      setRemoveFromPoolTarget(target),
    closeRemoveFromPool: () => setRemoveFromPoolTarget(null),
    openHire: (candidate: Candidate) => {
      // Most hires start soon after the decision, so default to today.
      setHireStartDate(Temporal.Now.plainDateISO().toString());
      setHireTarget(toTarget(candidate));
    },
    closeHire: resetHireDialog,
    openMarkNotHired: (candidate: Candidate) =>
      setMarkNotHiredTarget(toTarget(candidate)),
    closeMarkNotHired: () => setMarkNotHiredTarget(null),
    openReject: (candidate: Candidate) => {
      setRejectTarget(toTarget(candidate));
      setRejectionMessage(
        buildRejectionMessage({
          candidate,
          jobTitle: jobTitle ?? "",
          jobId,
        }),
      );
      setSendRejectionMessage(true);
    },
    closeReject: resetRejectDialog,
    // Mutations
    hireMutation,
    markNotHiredMutation,
    rejectCandidateMutation,
    setCandidateRejectionMutation,
    setOpenJobMutation,
    deleteJobMutation,
    removeFromPoolMutation,
    transitionStageMutation,
    reinstateCandidate: (candidate: Candidate) =>
      setCandidateRejectionMutation.mutate({
        organizationBusinessUnitId,
        jobPostId: jobId,
        userId: candidate.userId,
        rejected: false,
      }),
    transitionStage: ({
      candidate,
      stageId,
    }: {
      candidate: Candidate;
      stageId: string;
    }) =>
      transitionStageMutation.mutate({
        organizationBusinessUnitId,
        jobPostId: jobId,
        userId: candidate.userId,
        toStageId: stageId,
      }),
    invalidateCandidates,
  };
}

export type JobDashboardActions = ReturnType<typeof useJobDashboardActions>;
